import { inject, Injectable, Signal } from '@angular/core';
import { activeRecordFilter } from '@shared/constants/active-record-filter';
import { Entity } from '@shared/constants/entity';
import { VtSignalFormElement } from '@shared/dv-signal-form/interfaces/signal-form-element.config';
import * as f from '@shared/dv-signal-form/utils/signal-form-element.factory';
import { DamageReportFormModel } from '../../damage-reports-grid/interfaces';
import { DamageReportsMetadataService } from '@pages/damage-reports-page/services/damage-reports-metadata.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class DamageReportInformationTabService {
  private metadata = inject(DamageReportsMetadataService);
  private translate = inject(TranslateService);

  getDamageInformationElements =
    (): VtSignalFormElement<DamageReportFormModel>[] => [
      f.dropdown<DamageReportFormModel>({
        path: 'DamageReportStatusID',
        label: 'DamageReport.Status',
        colspan: 3,
        extra: {
          translateLabels: ['TranslationKey'],
          extraDisplayDataFields: ['DamageReportStatus'],
          flatDisplayField: {
            source: 'DamageReportStatus',
            target: 'TranslationKey',
          },
        },
        props: {
          idField: 'DamageReportStatusID',
          columns: [
            { field: 'TranslationKey' },
            { field: 'DamageReportStatusID', hide: true },
          ],
          isClearable: false,
          context: { entity: Entity.EnumDamageReportStatus },
        },
      }),
      f.datetime<DamageReportFormModel>({
        path: 'IncidentDateTime',
        label: 'DamageReport.IncidentDateTime',
        colspan: 3,
      }),
      f.text({
        path: 'CreatedByDescription',
        label: 'DamageReport.CreatedByDescription',
        colspan: 6,
      }),
      f.lineBreak(),
      f.dropdown<DamageReportFormModel>({
        path: 'RegistrationNumber',
        label: 'DamageReport.RegistrationNumber',
        colspan: 3,
        extra: {
          flatDisplayField: {
            source: 'RegistrationNumber',
            target: 'RegistrationNumber',
          },
        },
        props: {
          idField: 'RegistrationNumber',
          columns: [
            { field: 'RegistrationNumber' },
            { field: 'VehicleDescription', hide: true },
            { field: 'UserDescription', hide: true },
            { field: 'UserMobile', hide: true },
            { field: 'DefaultUserID', hide: true },
            { field: 'VehicleID', hide: true },
          ],
          isClearable: true,
          context: { entity: Entity.Vehicle, filterModel: activeRecordFilter },
        },
      }),
      f.dropdown<DamageReportFormModel>({
        path: 'TrackerObjectDescription',
        label: 'DamageReport.VehicleDescription',
        colspan: 3,
        extra: {
          flatDisplayField: {
            source: 'TrackerObjectDescription',
            target: 'VehicleDescription',
          },
        },
        props: {
          idField: 'VehicleDescription',
          columns: [
            { field: 'VehicleDescription' },
            { field: 'RegistrationNumber', hide: true },
            { field: 'UserDescription', hide: true },
            { field: 'UserMobile', hide: true },
            { field: 'DefaultUserID', hide: true },
            { field: 'VehicleID', hide: true },
          ],
          isClearable: true,
          context: { entity: Entity.Vehicle, filterModel: activeRecordFilter },
        },
      }),
      f.dropdown<DamageReportFormModel>({
        path: 'DriverID',
        label: 'DamageReport.DefaultDriver',
        colspan: 3,
        extra: {
          flatDisplayField: {
            source: 'DriverDescription',
            target: 'UserDescription',
          },
        },
        props: {
          idField: 'UserID',
          columns: [
            {
              field: 'UserDescription',
              valueGetter: item => {
                if (item.UserDescription && item.Username) {
                  return `${item.UserDescription} (${item.Username})`;
                }
                return item.UserDescription || item.Username || '-';
              },
            },
            { field: 'Username', hide: true },
            { field: 'UserID', hide: true },
            { field: 'Mobile', hide: true },
          ],
          isClearable: true,
          context: { entity: Entity.User },
        },
      }),
      f.phoneDropdown<DamageReportFormModel>({
        path: 'Mobile',
        label: 'DamageReport.Phone',
        colspan: 3,
      }),
      f.lineBreak(),
      f.text<DamageReportFormModel>({
        path: 'Location',
        label: 'DamageReport.Location',
        colspan: 3,
      }),
      f.dropdown<DamageReportFormModel>({
        path: 'WeatherCondition',
        label: 'DamageReport.WeatherCondition',
        colspan: 3,
        extra: { translateLabels: ['TranslationKey'] },
        props: {
          idField: 'WeatherConditionID',
          columns: [
            { field: 'TranslationKey' },
            { field: 'WeatherConditionID', hide: true },
          ],
          isMultiSelect: true,
          enableCheckboxes: true,
          isClearable: true,
          context: { entity: Entity.EnumWeatherCondition },
        },
      }),
      f.text<DamageReportFormModel>({
        path: 'ShortDescription',
        label: 'DamageReport.ShortDescription',
        colspan: 6,
      }),
      f.lineBreak(),
      f.numberInput({ path: 'DamageReportID', hide: true }),
      f.numberInput({ path: 'TrackerObjectID', hide: true }),
    ];

  getIncidentDetailsElements =
    (): VtSignalFormElement<DamageReportFormModel>[] => [
      f.dropdown<DamageReportFormModel>({
        path: 'IncidentTypeID',
        label: 'DamageReport.IncidentType',
        colspan: 4,
        extra: {
          translateLabels: ['TranslationKey'],
          extraDisplayDataFields: ['IncidentType'],
          flatDisplayField: {
            source: 'IncidentType',
            target: 'TranslationKey',
          },
        },
        props: {
          idField: 'IncidentTypeID',
          columns: [
            { field: 'TranslationKey' },
            { field: 'IncidentTypeID', hide: true },
          ],
          isClearable: true,
          context: { entity: Entity.EnumIncidentType },
        },
      }),
      f.dropdown<DamageReportFormModel>({
        path: 'MyVehicleIncidentAction',
        label: 'DamageReport.MyVehicleIncidentActionID',
        colspan: 4,
        extra: {
          translateLabels: ['TranslationKey'],
        },
        props: {
          idField: 'VehicleIncidentActionID',
          columns: [
            { field: 'TranslationKey' },
            { field: 'VehicleIncidentActionID', hide: true },
          ],
          isClearable: true,
          isMultiSelect: true,
          enableCheckboxes: true,
          context: { entity: Entity.EnumVehicleIncidentAction },
        },
      }),
      f.dropdown<DamageReportFormModel>({
        path: 'OtherVehicleIncidentAction',
        label: 'DamageReport.OtherVehicleIncidentActionID',
        colspan: 4,
        extra: {
          translateLabels: ['TranslationKey'],
        },
        props: {
          idField: 'VehicleIncidentActionID',
          columns: [
            { field: 'TranslationKey' },
            { field: 'VehicleIncidentActionID', hide: true },
          ],
          isClearable: true,
          isMultiSelect: true,
          enableCheckboxes: true,
          context: { entity: Entity.EnumVehicleIncidentAction },
        },
      }),
      f.lineBreak(),
      f.textarea<DamageReportFormModel>({
        path: 'IncidentDescription',
        label: 'DamageReport.IncidentDescription',
        colspan: 12,
      }),
    ];

  getDamageDetailsElements = (): Signal<
    VtSignalFormElement<DamageReportFormModel>[]
  > =>
    f.computedFormElements(() => [
      f.dropdown<DamageReportFormModel>({
        path: 'DamageLocation',
        label: 'DamageReport.DamageLocation',
        colspan: 12,
        props: {
          idField: 'DamageLocationID',
          items: (this.metadata.damageLocationResource.value() || []).map(
            locationItem => ({
              ...locationItem,
              TranslationKey: this.translate.instant(
                locationItem.TranslationKey || ''
              ),
            })
          ),
          columns: [
            { field: 'TranslationKey' },
            { field: 'DamageLocationID', hide: true },
          ],
          isClearable: false,
          isMultiSelect: true,
          enableCheckboxes: true,
        },
      }),
      f.lineBreak(),
      f.textarea<DamageReportFormModel>({
        path: 'DamageDescription',
        label: 'DamageReport.DamageDescription',
        colspan: 12,
      }),
      f.lineBreak(),
      f.radio<DamageReportFormModel>({
        path: 'DamageSeverityID',
        label: 'DamageReport.Severity',
        colspan: 12,
        extra: { translateLabels: ['TranslationKey'] },
        props: {
          items: (this.metadata.damageSeverityResource.value() || []).map(
            severityItem => ({
              ...severityItem,
              TranslationKey: this.translate.instant(
                severityItem.TranslationKey || ''
              ),
            })
          ),
          labelKey: 'TranslationKey',
          valueKey: 'DamageSeverityID',
          direction: 'vertical',
          gridSize: 3,
        },
      }),
      f.lineBreak(),
    ]);

  getOtherVehicleElements =
    (): VtSignalFormElement<DamageReportFormModel>[] => [
      f.radio<DamageReportFormModel>({
        path: 'OtherVehicleInvolved',
        colspan: 12,
        extra: { ignoreInRequest: true },
        props: {
          valueKey: 'id',
          items: [
            { id: true, label: 'General.Yes' },
            { id: false, label: 'General.No' },
          ],
          direction: 'horizontal',
          gridSize: 2,
        },
      }),
      f.text<DamageReportFormModel>({
        path: 'OtherVehicleRegistrationNumber',
        label: 'DamageReport.OtherVehicleRegistrationNumber',
        colspan: 4,
      }),
      f.text<DamageReportFormModel>({
        path: 'OtherVehicleDriverName',
        label: 'DamageReport.OtherVehicleDriverName',
        colspan: 4,
      }),
      f.text<DamageReportFormModel>({
        path: 'OtherVehicleDriverContactInfo',
        label: 'DamageReport.OtherVehicleDriverContactInfo',
        colspan: 4,
      }),
      f.lineBreak(),
      f.text<DamageReportFormModel>({
        path: 'OtherVehicleInsuranceCompany',
        label: 'DamageReport.OtherVehicleInsuranceCompany',
        colspan: 4,
      }),
      f.text<DamageReportFormModel>({
        path: 'OtherVehicleDriverLicenseNumber',
        label: 'DamageReport.OtherVehicleDriverLicenseNumber',
        colspan: 4,
      }),
      f.text<DamageReportFormModel>({
        path: 'OtherVehicleCountryCode',
        label: 'DamageReport.OtherVehicleCountryCode',
        colspan: 4,
      }),
    ];

  additionalInformationElements =
    (): VtSignalFormElement<DamageReportFormModel>[] => [
      f.radio<DamageReportFormModel>({
        path: 'PoliceInvolved',
        label: 'DamageReport.PoliceInvolved',
        colspan: 4,
        props: {
          valueKey: 'id',
          items: [
            { id: true, label: 'General.Yes' },
            { id: false, label: 'General.No' },
          ],
          direction: 'horizontal',
          gridSize: 2,
        },
      }),
      f.text<DamageReportFormModel>({
        path: 'PoliceReportNumber',
        label: 'DamageReport.PoliceReportNumber',
        colspan: 8,
      }),
      f.lineBreak(),
      f.radio<DamageReportFormModel>({
        path: 'PersonalInjury',
        label: 'DamageReport.PersonalInjury',
        colspan: 12,
        props: {
          valueKey: 'id',
          items: [
            { id: true, label: 'General.Yes' },
            { id: false, label: 'General.No' },
          ],
          direction: 'horizontal',
          gridSize: 2,
        },
      }),
      f.lineBreak(),
      f.radio<DamageReportFormModel>({
        path: 'WitnessInvolved',
        label: 'DamageReport.WitnessInvolved',
        colspan: 4,
        extra: { ignoreInRequest: true },
        props: {
          valueKey: 'id',
          items: [
            { id: true, label: 'General.Yes' },
            { id: false, label: 'General.No' },
          ],
          direction: 'horizontal',
          gridSize: 2,
        },
      }),
      f.text<DamageReportFormModel>({
        path: 'WitnessName',
        label: 'DamageReport.WitnessName',
        colspan: 4,
      }),
      f.text<DamageReportFormModel>({
        path: 'WitnessContactInfo',
        label: 'DamageReport.WitnessContactInfo',
        colspan: 4,
      }),
    ];
}
